import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import AutoCell from "../components/AutoCell";
import SaveDialog from "../components/SaveDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useLang } from "../context/LangContext";
import { useDirtyGuard, useUnsavedChangesWarning } from "../context/DirtyContext";
import { api, formatApiError } from "../lib/api";
import { Calendar, Calculator, Save, Loader2, Info } from "lucide-react";
import { toast } from "sonner";

const ROW_COUNT = 15;

const num = (v) => {
    if (v === "" || v === null || v === undefined) return 0;
    const n = parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
};

const fmt = (n) => {
    if (!Number.isFinite(n) || n === 0) return "";
    // Trim trailing zeros while keeping up to 3 decimals.
    const s = n.toFixed(3);
    return s.replace(/\.?0+$/, "");
};

const emptyRow = (idx) => ({ idx: idx + 1, desc: "", gram: "", cash: "", transfer: "", bank: "" });
const emptyPurchaseRow = (idx) => ({ idx: idx + 1, desc: "", gram: "", cash: "" });

const initialSection = () =>
    Array.from({ length: ROW_COUNT }, (_, i) => emptyRow(i));
const initialPurchase = () =>
    Array.from({ length: ROW_COUNT }, (_, i) => emptyPurchaseRow(i));

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function DailyMovement() {
    const { t, lang } = useLang();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get("id");

    const [date, setDate] = useState(todayIso());
    const [sales, setSales] = useState(initialSection());
    const [expenses, setExpenses] = useState(initialSection());
    const [purchases, setPurchases] = useState(initialPurchase());
    const [carryNotice, setCarryNotice] = useState(null); // "carried" | "none" | null
    const [calcResult, setCalcResult] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    // Edit-mode: when ?id=... is set, we load the existing editable ledger
    // and use PUT instead of POST on save.
    const [loadingEdit, setLoadingEdit] = useState(!!editId);

    const markDirty = () => {
        setIsDirty(true);
        setCalcResult(null);
    };

    /** Append a new empty row at the end of the section (#5). */
    const addRow = (setter, factory) => () => {
        setter((rows) => [...rows, factory(rows.length)]);
        markDirty();
    };

    // ── Auto carry-over from previous day on mount ──
    useEffect(() => {
        if (editId) return; // edit mode: skip carry-over, we load the saved doc
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get("/ledgers/daily/last-balance");
                if (cancel) return;
                if (data?.found) {
                    setSales((rows) => {
                        const next = rows.slice();
                        next[0] = {
                            ...next[0],
                            desc: t.dailyExportLabel,
                            cash: fmt(data.sales?.cash || 0),
                            transfer: fmt(data.sales?.transfer || 0),
                            bank: fmt(data.sales?.bank || 0),
                        };
                        return next;
                    });
                    setPurchases((rows) => {
                        const next = rows.slice();
                        next[0] = {
                            ...next[0],
                            desc: t.dailyExportLabel,
                            gram: fmt(data.purchases?.weight || 0),
                        };
                        return next;
                    });
                    setCarryNotice("carried");
                } else {
                    setSales((rows) => {
                        const next = rows.slice();
                        next[0] = { ...next[0], desc: t.dailyExportLabel };
                        return next;
                    });
                    setPurchases((rows) => {
                        const next = rows.slice();
                        next[0] = { ...next[0], desc: t.dailyExportLabel };
                        return next;
                    });
                    setExpenses((rows) => {
                        const next = rows.slice();
                        next[0] = { ...next[0], desc: t.dailyExportLabel };
                        return next;
                    });
                    setCarryNotice("none");
                }
                // expenses also gets export label (no carryover for expenses)
                setExpenses((rows) => {
                    const next = rows.slice();
                    next[0] = { ...next[0], desc: t.dailyExportLabel };
                    return next;
                });
            } catch {
                setCarryNotice("none");
            }
        })();
        return () => {
            cancel = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Edit mode: load an existing editable daily ledger ──
    useEffect(() => {
        if (!editId) return;
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get(`/ledgers/${editId}`);
                if (cancel) return;
                if ((data?.save_status || "final") === "final") {
                    toast.error(t.recordLockedFinal || "السجل مغلق نهائياً");
                    navigate(`/history/daily/${editId}`, { replace: true });
                    return;
                }
                const dd = data?.daily_data || {};
                if (Array.isArray(dd?.sales?.rows) && dd.sales.rows.length) {
                    setSales(dd.sales.rows);
                }
                if (Array.isArray(dd?.expenses?.rows) && dd.expenses.rows.length) {
                    setExpenses(dd.expenses.rows);
                }
                if (Array.isArray(dd?.purchases?.rows) && dd.purchases.rows.length) {
                    setPurchases(dd.purchases.rows);
                }
                if (data.daily_date) setDate(data.daily_date);
                setCarryNotice(null);
                setIsDirty(false);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || e.message);
                navigate("/history/daily", { replace: true });
            } finally {
                if (!cancel) setLoadingEdit(false);
            }
        })();
        return () => { cancel = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editId]);

    const updateRow = (setter) => (idx, field, value) => {
        setter((rows) => {
            const next = rows.slice();
            next[idx] = { ...next[idx], [field]: value };
            return next;
        });
        markDirty();
    };

    // ── Calculate totals/spending/balances ──
    const calculate = () => {
        const sumCol = (rows, col) => rows.reduce((s, r) => s + num(r[col]), 0);

        const salesTotalCash = sumCol(sales, "cash");
        const salesTotalTransfer = sumCol(sales, "transfer");
        const salesTotalBank = sumCol(sales, "bank");

        const expTotalCash = sumCol(expenses, "cash");
        const expTotalTransfer = sumCol(expenses, "transfer");
        const expTotalBank = sumCol(expenses, "bank");
        const expTotalGram = sumCol(expenses, "gram");

        const purTotalCash = sumCol(purchases, "cash");
        const purTotalWeight = sumCol(purchases, "gram");

        // Sales Spending breakdown (avoid double-counting per spec #49):
        //   Cash spending     = Expenses Cash + Purchases Cash
        //   Transfer spending = Expenses Transfer
        //   Bank spending     = Expenses Bank
        const salesSpendingCash = expTotalCash + purTotalCash;
        const salesSpendingTransfer = expTotalTransfer;
        const salesSpendingBank = expTotalBank;

        // Balances per column = Total − Spending
        const salesBalCash = salesTotalCash - salesSpendingCash;
        const salesBalTransfer = salesTotalTransfer - salesSpendingTransfer;
        const salesBalBank = salesTotalBank - salesSpendingBank;

        const purBalWeight = purTotalWeight - expTotalGram;

        const result = {
            sales: {
                total: { cash: salesTotalCash, transfer: salesTotalTransfer, bank: salesTotalBank },
                spending: {
                    cash: salesSpendingCash,
                    transfer: salesSpendingTransfer,
                    bank: salesSpendingBank,
                },
                balance: { cash: salesBalCash, transfer: salesBalTransfer, bank: salesBalBank },
            },
            expenses: {
                total: {
                    cash: expTotalCash,
                    transfer: expTotalTransfer,
                    bank: expTotalBank,
                    weight: expTotalGram,
                },
            },
            purchases: {
                total: { cash: purTotalCash, weight: purTotalWeight },
                spending: { weight: expTotalGram },
                balance: { weight: purBalWeight },
            },
        };
        setCalcResult(result);
        toast.success(t.dailyCalculateBtn + " ✓");
    };

    const hasAnyEntry = useMemo(() => {
        const filledSales = sales.some(
            (r, i) => i > 0 && (r.desc || r.gram || r.cash || r.transfer || r.bank)
        );
        const filledExpenses = expenses.some(
            (r, i) => i > 0 && (r.desc || r.gram || r.cash || r.transfer || r.bank)
        );
        const filledPurchases = purchases.some(
            (r, i) => i > 0 && (r.desc || r.gram || r.cash)
        );
        // Also count first-row carry over as content
        const sCarry = sales[0]?.cash || sales[0]?.transfer || sales[0]?.bank;
        const pCarry = purchases[0]?.gram;
        return filledSales || filledExpenses || filledPurchases || sCarry || pCarry;
    }, [sales, expenses, purchases]);

    const requestSave = () => {
        if (!date) {
            toast.error(t.dailyDateRequired);
            return;
        }
        if (!hasAnyEntry) {
            toast.error(t.dailyEmpty);
            return;
        }
        if (!calcResult) {
            calculate();
        }
        setConfirmOpen(true);
    };

    // Register page with the global dirty-guard so navigation prompts work.
    useDirtyGuard({
        isDirty,
        save: async () => {
            requestSave();
            return false; // user must confirm via dialog
        },
        title: t.dailySaveBtn,
    });
    useUnsavedChangesWarning(isDirty);

    const doSave = async (mode = "final") => {
        setSaving(true);
        try {
            const dailyData = {                sales: {
                    rows: sales,
                    total: calcResult?.sales.total || { cash: 0, transfer: 0, bank: 0 },
                    spending: calcResult?.sales.spending || { cash: 0, transfer: 0, bank: 0 },
                    balance: calcResult?.sales.balance || { cash: 0, transfer: 0, bank: 0 },
                },
                expenses: {
                    rows: expenses,
                    total: calcResult?.expenses.total || {
                        cash: 0,
                        transfer: 0,
                        bank: 0,
                        weight: 0,
                    },
                },
                purchases: {
                    rows: purchases,
                    total: calcResult?.purchases.total || { cash: 0, weight: 0 },
                    spending: calcResult?.purchases.spending || { weight: 0 },
                    balance: calcResult?.purchases.balance || { weight: 0 },
                },
            };
            const payload = {
                kind: "daily",
                client_name: `${t.dailyPageTitle} — ${date}`,
                credit_rows: [],
                debit_rows: [],
                daily_data: dailyData,
                daily_date: date,
                save_status: mode === "editable" ? "editable" : "final",
            };
            if (editId) {
                await api.put(`/ledgers/${editId}`, payload);
            } else {
                await api.post("/ledgers", payload);
            }
            toast.success(mode === "editable" ? t.ledgerSavedEditable : t.dailySavedToast);
            setIsDirty(false);
            setConfirmOpen(false);
            navigate("/history/daily");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message || t.dailySaveFail);
        } finally {
            setSaving(false);
        }
    };

    /** Discard all in-memory changes and exit without persisting. */
    const doIgnore = () => {
        setIsDirty(false);
        setConfirmOpen(false);
        navigate(editId ? `/history/daily/${editId}` : "/history/daily");
    };

    return (
        <AppShell>
            {/* Header */}
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3 sm:mb-6">
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">
                        {t.dailyKicker}
                    </p>
                    <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">
                        {t.dailyPageTitle}
                    </h1>
                </div>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm">
                        <Calendar className="h-4 w-4 text-amber-700" />
                        <span className="text-neutral-600">{t.dailyDateLabel}</span>
                        <Input
                            type="date"
                            value={date}
                            onChange={(e) => {
                                setDate(e.target.value);
                                setIsDirty(true);
                            }}
                            className="h-8 w-36 border-0 p-0 font-numbers text-sm shadow-none focus-visible:ring-0"
                            data-testid="daily-date-input"
                        />
                    </label>
                </div>
            </div>

            {/* Carry-over notice */}
            {carryNotice === "carried" ? (
                <div
                    className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-sm text-emerald-800"
                    data-testid="daily-carry-notice"
                >
                    <Info className="h-4 w-4" />
                    <span>{t.dailyCarriedFromYesterday}</span>
                </div>
            ) : carryNotice === "none" ? (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-600">
                    <Info className="h-4 w-4" />
                    <span>{t.dailyNoPrior}</span>
                </div>
            ) : null}

            {/* Three sections grid */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" dir={lang === "ar" ? "rtl" : "ltr"}>
                <SectionTable
                    title={t.dailySalesTitle}
                    rows={sales}
                    onChange={updateRow(setSales)}
                    onAddRow={addRow(setSales, emptyRow)}
                    columns={["cash", "transfer", "bank"]}
                    t={t}
                    testIdPrefix="sales"
                    totalsRow={calcResult?.sales.total}
                    spendingRow={calcResult?.sales.spending}
                    balanceRow={calcResult?.sales.balance}
                    showGram={true}
                    showSpending={true}
                />
                <SectionTable
                    title={t.dailyExpensesTitle}
                    rows={expenses}
                    onChange={updateRow(setExpenses)}
                    onAddRow={addRow(setExpenses, emptyRow)}
                    columns={["cash", "transfer", "bank"]}
                    t={t}
                    testIdPrefix="expenses"
                    totalsRow={calcResult?.expenses.total}
                    showGram={true}
                    showSpending={false}
                />
                <PurchasesTable
                    title={t.dailyPurchasesTitle}
                    rows={purchases}
                    onChange={updateRow(setPurchases)}
                    onAddRow={addRow(setPurchases, emptyPurchaseRow)}
                    t={t}
                    testIdPrefix="purchases"
                    totalsRow={calcResult?.purchases.total}
                    spendingRow={calcResult?.purchases.spending}
                    balanceRow={calcResult?.purchases.balance}
                />
            </div>

            {/* Action bar */}
            <div className="sticky bottom-4 z-20 mt-6 flex flex-wrap items-center justify-end gap-3 rounded-2xl border border-neutral-200 bg-white/95 p-3 shadow-lg backdrop-blur sm:p-4">
                <Button
                    type="button"
                    variant="outline"
                    onClick={calculate}
                    className="h-11 gap-2 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                    data-testid="daily-calc-btn"
                >
                    <Calculator className="h-4 w-4" />
                    {t.dailyCalculateBtn}
                </Button>
                <Button
                    type="button"
                    onClick={requestSave}
                    disabled={saving}
                    className="h-11 gap-2 bg-neutral-900 text-white hover:bg-neutral-800"
                    data-testid="daily-save-btn"
                >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {t.dailySaveBtn}
                </Button>
            </div>

            <SaveDialog
                open={confirmOpen}
                onOpenChange={setConfirmOpen}
                saving={saving}
                onSaveFinal={() => doSave("final")}
                onSaveEditable={() => doSave("editable")}
                onIgnore={doIgnore}
                testIdPrefix="daily"
            />
        </AppShell>
    );
}

// ─── Section Table (Sales / Expenses) ───
function SectionTable({
    title,
    rows,
    onChange,
    onAddRow,
    columns, // ["cash","transfer","bank"]
    t,
    testIdPrefix,
    totalsRow,
    spendingRow,
    balanceRow,
    showSpending,
    readOnly = false,
}) {
    return (
        <div
            className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm"
            data-testid={`daily-section-${testIdPrefix}`}
        >
            <div className="bg-neutral-900 px-3 py-2.5 text-center font-heading text-base font-bold text-white">
                {title}
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-amber-50 text-amber-900">
                        <tr className="text-center">
                            <th className="border border-neutral-200 px-1.5 py-1.5 font-bold">{t.dailyColCash}</th>
                            <th className="border border-neutral-200 px-1.5 py-1.5 font-bold">{t.dailyColTransfer}</th>
                            <th className="border border-neutral-200 px-1.5 py-1.5 font-bold">{t.dailyColBank}</th>
                            <th className="border border-neutral-200 px-1.5 py-1.5 font-bold w-10">{t.dailyColIdx}</th>
                            <th className="border border-neutral-200 px-1.5 py-1.5 font-bold">{t.dailyColDesc}</th>
                            <th className="border border-neutral-200 px-1.5 py-1.5 font-bold">{t.dailyColGram}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, idx) => {
                            const isExport = idx === 0;
                            return (
                                <tr key={idx} className={isExport ? "bg-amber-50/40" : ""}>
                                    <td className="border border-neutral-200 align-top p-0">
                                        <CellInput value={r.cash} onChange={(v) => onChange(idx, "cash", v)} testId={`${testIdPrefix}-${idx}-cash`} readOnly={readOnly} numeric />
                                    </td>
                                    <td className="border border-neutral-200 align-top p-0">
                                        <CellInput value={r.transfer} onChange={(v) => onChange(idx, "transfer", v)} testId={`${testIdPrefix}-${idx}-transfer`} readOnly={readOnly} numeric />
                                    </td>
                                    <td className="border border-neutral-200 align-top p-0">
                                        <CellInput value={r.bank} onChange={(v) => onChange(idx, "bank", v)} testId={`${testIdPrefix}-${idx}-bank`} readOnly={readOnly} numeric />
                                    </td>
                                    <td className="border border-neutral-200 bg-amber-50/60 px-1 py-1 text-center font-numbers font-semibold text-neutral-700">
                                        {idx + 1}
                                    </td>
                                    <td className="border border-neutral-200 align-top p-0">
                                        <CellInput value={r.desc} onChange={(v) => onChange(idx, "desc", v)} testId={`${testIdPrefix}-${idx}-desc`} readOnly={readOnly} />
                                    </td>
                                    <td className="border border-neutral-200 align-top p-0">
                                        <CellInput value={r.gram} onChange={(v) => onChange(idx, "gram", v)} testId={`${testIdPrefix}-${idx}-gram`} readOnly={readOnly} numeric />
                                    </td>
                                </tr>
                            );
                        })}
                        {/* Bottom totals — header */}
                        <tr className="bg-neutral-100 text-[11px] font-bold text-neutral-700">
                            <td className="border border-neutral-200 px-1.5 py-1 text-center">{t.dailyColCash}</td>
                            <td className="border border-neutral-200 px-1.5 py-1 text-center">{t.dailyColTransfer}</td>
                            <td className="border border-neutral-200 px-1.5 py-1 text-center">{t.dailyColBank}</td>
                            <td className="border border-neutral-200" colSpan={2}></td>
                            <td className="border border-neutral-200 px-1.5 py-1 text-center">{t.dailyColWeight}</td>
                        </tr>
                        <TotalRow
                            label={t.dailyTotalLabel}
                            cash={totalsRow?.cash}
                            transfer={totalsRow?.transfer}
                            bank={totalsRow?.bank}
                            weight={totalsRow?.weight}
                            testIdPrefix={`${testIdPrefix}-total`}
                        />
                        {showSpending ? (
                            <TotalRow
                                label={t.dailySpendingLabel}
                                cash={spendingRow?.cash}
                                transfer={spendingRow?.transfer}
                                bank={spendingRow?.bank}
                                testIdPrefix={`${testIdPrefix}-spending`}
                                highlightAll
                            />
                        ) : null}
                        {balanceRow ? (
                            <TotalRow
                                label={t.dailyBalanceLabel}
                                cash={balanceRow.cash}
                                transfer={balanceRow.transfer}
                                bank={balanceRow.bank}
                                testIdPrefix={`${testIdPrefix}-balance`}
                                highlightAll
                            />
                        ) : null}
                    </tbody>
                </table>
            </div>
            {!readOnly && onAddRow ? (
                <div className="border-t border-neutral-100 bg-neutral-50/60 px-3 py-2">
                    <button
                        type="button"
                        onClick={onAddRow}
                        className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-3 py-1 text-xs font-semibold text-neutral-700 shadow-sm hover:bg-neutral-100"
                        data-testid={`daily-add-row-${testIdPrefix}`}
                    >
                        + {t.advAddRow}
                    </button>
                </div>
            ) : null}
        </div>
    );
}

// ─── Purchases Table (no transfer, no bank) ───
function PurchasesTable({ title, rows, onChange, onAddRow, t, testIdPrefix, totalsRow, spendingRow, balanceRow, readOnly = false }) {
    return (
        <div
            className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm"
            data-testid={`daily-section-${testIdPrefix}`}
        >
            <div className="bg-neutral-900 px-3 py-2.5 text-center font-heading text-base font-bold text-white">
                {title}
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-amber-50 text-amber-900">
                        <tr className="text-center">
                            <th className="border border-neutral-200 px-1.5 py-1.5 font-bold">{t.dailyColCash}</th>
                            <th className="border border-neutral-200 px-1.5 py-1.5 font-bold w-10">{t.dailyColIdx}</th>
                            <th className="border border-neutral-200 px-1.5 py-1.5 font-bold">{t.dailyColDesc}</th>
                            <th className="border border-neutral-200 px-1.5 py-1.5 font-bold">{t.dailyColGram}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, idx) => {
                            const isExport = idx === 0;
                            return (
                                <tr key={idx} className={isExport ? "bg-amber-50/40" : ""}>
                                    <td className="border border-neutral-200 align-top p-0">
                                        <CellInput value={r.cash} onChange={(v) => onChange(idx, "cash", v)} testId={`${testIdPrefix}-${idx}-cash`} readOnly={readOnly} numeric />
                                    </td>
                                    <td className="border border-neutral-200 bg-amber-50/60 px-1 py-1 text-center font-numbers font-semibold text-neutral-700">
                                        {idx + 1}
                                    </td>
                                    <td className="border border-neutral-200 align-top p-0">
                                        <CellInput value={r.desc} onChange={(v) => onChange(idx, "desc", v)} testId={`${testIdPrefix}-${idx}-desc`} readOnly={readOnly} />
                                    </td>
                                    <td className="border border-neutral-200 align-top p-0">
                                        <CellInput value={r.gram} onChange={(v) => onChange(idx, "gram", v)} testId={`${testIdPrefix}-${idx}-gram`} readOnly={readOnly} numeric />
                                    </td>
                                </tr>
                            );
                        })}
                        <tr className="bg-neutral-100 text-[11px] font-bold text-neutral-700">
                            <td className="border border-neutral-200 px-1.5 py-1 text-center">{t.dailyColCash}</td>
                            <td className="border border-neutral-200" colSpan={2}></td>
                            <td className="border border-neutral-200 px-1.5 py-1 text-center">{t.dailyColWeight}</td>
                        </tr>
                        <PurchaseTotalRow label={t.dailyTotalLabel} cash={totalsRow?.cash} weight={totalsRow?.weight} testIdPrefix={`${testIdPrefix}-total`} />
                        {spendingRow ? (
                            <PurchaseTotalRow
                                label={t.dailySpendingLabel}
                                weight={spendingRow.weight}
                                testIdPrefix={`${testIdPrefix}-spending`}
                                highlightWeight
                            />
                        ) : null}
                        {balanceRow ? (
                            <PurchaseTotalRow label={t.dailyBalanceLabel} weight={balanceRow.weight} testIdPrefix={`${testIdPrefix}-balance`} highlightAll />
                        ) : null}
                    </tbody>
                </table>
            </div>
            {!readOnly && onAddRow ? (
                <div className="border-t border-neutral-100 bg-neutral-50/60 px-3 py-2">
                    <button
                        type="button"
                        onClick={onAddRow}
                        className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-3 py-1 text-xs font-semibold text-neutral-700 shadow-sm hover:bg-neutral-100"
                        data-testid={`daily-add-row-${testIdPrefix}`}
                    >
                        + {t.advAddRow}
                    </button>
                </div>
            ) : null}
        </div>
    );
}

function TotalRow({ label, cash, transfer, bank, weight, testIdPrefix, highlightCash, highlightAll }) {
    const cellCls = (highlight) =>
        `border border-neutral-200 px-1.5 py-1.5 text-center font-numbers font-semibold ${
            highlight ? "bg-emerald-50 text-emerald-800" : "text-neutral-700"
        }`;
    return (
        <tr className="bg-white">
            <td className={cellCls(highlightAll || highlightCash)} data-testid={`${testIdPrefix}-cash`}>
                {fmt(cash || 0)}
            </td>
            <td className={cellCls(highlightAll)} data-testid={`${testIdPrefix}-transfer`}>
                {fmt(transfer || 0)}
            </td>
            <td className={cellCls(highlightAll)} data-testid={`${testIdPrefix}-bank`}>
                {fmt(bank || 0)}
            </td>
            <td className="border border-neutral-200 bg-neutral-900 px-1.5 py-1.5 text-center text-[11px] font-bold text-white" colSpan={2}>
                {label}
            </td>
            <td className={cellCls(false)} data-testid={`${testIdPrefix}-weight`}>
                {fmt(weight || 0)}
            </td>
        </tr>
    );
}

function PurchaseTotalRow({ label, cash, weight, testIdPrefix, highlightAll, highlightWeight }) {
    const cellCls = (h) =>
        `border border-neutral-200 px-1.5 py-1.5 text-center font-numbers font-semibold ${
            h ? "bg-emerald-50 text-emerald-800" : "text-neutral-700"
        }`;
    return (
        <tr className="bg-white">
            <td className={cellCls(highlightAll)} data-testid={`${testIdPrefix}-cash`}>
                {fmt(cash || 0)}
            </td>
            <td className="border border-neutral-200 bg-neutral-900 px-1.5 py-1.5 text-center text-[11px] font-bold text-white" colSpan={2}>
                {label}
            </td>
            <td className={cellCls(highlightAll || highlightWeight)} data-testid={`${testIdPrefix}-weight`}>
                {fmt(weight || 0)}
            </td>
        </tr>
    );
}

function CellInput({ value, onChange, testId, readOnly, numeric }) {
    return (
        <AutoCell
            value={value || ""}
            onChange={onChange}
            readOnly={readOnly}
            numeric={numeric}
            data-testid={testId}
            className={`text-xs sm:text-sm focus:bg-amber-50/70 ${readOnly ? "cursor-default" : ""}`}
        />
    );
}

export { SectionTable, PurchasesTable };
